// The orders an event's allocation list can be read in.
//
// Only the event-wide view offers a choice: the other three views of allocations sit
// inside a record whose own ordering is fixed by the server
// (core/inventory_allocations.sort_key). An event's kit list, though, is read four
// different ways — item by item to check the list off, person by person to hand it
// out, by role to kit out a group, and by location to fetch it off the shelves — and
// the chosen order is what the Excel and PDF exports carry, since the printed
// checklist is the point of choosing it.
//
// Kept out of the component so it can be exercised on its own, and because the four
// orders are a piece of the domain's vocabulary rather than a detail of one table.

import { ATTENDANCE_ROLES } from "./constants";

export const ALLOCATION_SORT_ITEM = "Item name";
export const ALLOCATION_SORT_PERSON = "Person";
export const ALLOCATION_SORT_ROLE = "Event role";
export const ALLOCATION_SORT_LOCATION = "Location";
// The container an item sits directly in, then its serial number: a wardrobe of
// numbered clothing is walked wardrobe by wardrobe, then down the numbers. Unlike
// Location it ignores a free-text location — only a real container counts.
export const ALLOCATION_SORT_CONTAINER = "Container";

// In dropdown order, the first being the default: a list is most often just checked
// off against the kit itself.
export const ALLOCATION_SORTS = [
  ALLOCATION_SORT_ITEM, ALLOCATION_SORT_PERSON,
  ALLOCATION_SORT_ROLE, ALLOCATION_SORT_LOCATION, ALLOCATION_SORT_CONTAINER,
];

// Case-insensitive, and "Bag 2" before "Bag 10" — the same comparator the inventory
// pages use, which matters most for serial numbers.
const cmpText = (a, b) =>
  (a || "").localeCompare(b || "", undefined, { numeric: true, sensitivity: "base" });

const itemName = (r) => r.item?.name;
const serial = (r) => r.item?.serial_number;
const place = (r) => r.item?.location;
const container = (r) => r.item?.container;

// Sorting "by person" means sorting by surname; the composed "Forename Surname"
// can't be, which is why the read model carries the parts as well as the whole.
const personKey = (r) => `${r.person?.surname || ""} ${r.person?.forename || ""}`;

const roleText = (r) => (r.person?.role || "").trim();

// Roles rank in ATTENDANCE_ROLES order (Clinical Lead first) rather than
// alphabetically: that list is ordered deliberately, and grouping a kit list by role
// only helps if the grouping is the one the team thinks in.
//
// Three bands, because the stored roles are free strings and the data holds values
// the app no longer offers ("Aid Station Medic"): the current roles in their own
// order, then anything else — grouped with its own kind by the comparator's next
// term rather than scattered — then no role at all, which is what an allocation to
// somebody who isn't on the event has.
const roleRank = (r) => {
  const role = roleText(r);
  const i = ATTENDANCE_ROLES.indexOf(role);
  if (i !== -1) return i;
  return role ? ATTENDANCE_ROLES.length : ATTENDANCE_ROLES.length + 1;
};

// Items in no container trail the ones you can go and fetch.
const blankLast = (v) => ((v || "").trim() ? 0 : 1);

// Every order but the first is tie-broken on serial number then item name, so two of
// the same thing in one person's hands still read in a fixed order.
const byItem = (a, b) => cmpText(itemName(a), itemName(b));
const bySerialThenItem = (a, b) => cmpText(serial(a), serial(b)) || byItem(a, b);

const COMPARATORS = {
  [ALLOCATION_SORT_ITEM]: byItem,
  [ALLOCATION_SORT_PERSON]: (a, b) =>
    cmpText(personKey(a), personKey(b)) || bySerialThenItem(a, b),
  // The role text itself tie-breaks before the person, which is what keeps two
  // rows holding the same off-list role together (see roleRank).
  [ALLOCATION_SORT_ROLE]: (a, b) =>
    (roleRank(a) - roleRank(b)) || cmpText(roleText(a), roleText(b))
    || cmpText(personKey(a), personKey(b)) || bySerialThenItem(a, b),
  [ALLOCATION_SORT_LOCATION]: (a, b) =>
    (blankLast(place(a)) - blankLast(place(b))) || cmpText(place(a), place(b))
    || bySerialThenItem(a, b),
  // Two containers can share a name (a "Front pocket" in each of two bags), so the
  // full path tie-breaks before the serial number to keep each one's contents together.
  [ALLOCATION_SORT_CONTAINER]: (a, b) =>
    (blankLast(container(a)) - blankLast(container(b))) || cmpText(container(a), container(b))
    || cmpText(place(a), place(b)) || bySerialThenItem(a, b),
};

/** The comparator for one of ALLOCATION_SORTS (item name for anything unknown). */
export const allocationComparator = (sortBy) =>
  COMPARATORS[sortBy] || COMPARATORS[ALLOCATION_SORT_ITEM];
